import { useState } from 'react'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card'
import { Separator } from '../../components/ui/separator'
import { PureGoogleSigninButton, PureMicrosoftSigninButton } from './pure-signin-buttons'

interface PureLoginFormProps {
  onEmailLogin?: (email: string) => void
  onGoogleLogin?: (credential: string) => void
  onMicrosoftLogin?: (token: string) => void
  onError?: (error: Error) => void
  className?: string
  mode?: 'login' | 'signup'
  onToggleMode?: () => void
}

export function PureLoginForm({ 
  onEmailLogin, 
  onGoogleLogin, 
  onMicrosoftLogin,
  onError,
  className = "",
  mode = 'login',
  onToggleMode
}: PureLoginFormProps) {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !email.includes('@')) return
    
    setIsLoading(true)
    try {
      await onEmailLogin?.(email)
      setEmail('')
    } finally {
      setIsLoading(false)
    }
  }

  const isSignup = mode === 'signup'

  return (
    <Card className={`w-full ${className}`}>
      <CardHeader className="space-y-1 text-center">
        <CardTitle className="text-2xl">{isSignup ? 'Sign Up' : 'Sign In'}</CardTitle>
        <CardDescription>
          {isSignup 
            ? 'Create your account to continue' 
            : 'Sign in to your account to continue'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-6">
        <div className="space-y-3">
          <PureGoogleSigninButton 
            className="w-full"
            onSuccess={onGoogleLogin}
            onError={onError}
          />
          <PureMicrosoftSigninButton 
            className="w-full"
            onSuccess={onMicrosoftLogin}
            onError={onError}
          />
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
            />
          </div>
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Sending...' : isSignup ? 'Create Account' : 'Send Magic Link'}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col items-center gap-4 px-6 pb-6">
        <p className="text-sm text-muted-foreground text-center">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
        <div className="text-sm text-center">
          {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
          <Button 
            variant="link" 
            className="p-0 h-auto font-normal"
            onClick={onToggleMode}
          >
            {isSignup ? 'Sign in' : 'Sign up'}
          </Button>
        </div>
      </CardFooter>
    </Card>
  )
} 